// Data-access client targeting the Hono control-api through the same-origin /api prefix.
// The web app never talks to the Go core-engine directly (D-0001 / C-0002) and
// holds no domain persistence code: everything here is fetch + types.
import type {
  Amendment,
  AmendmentCreate,
  ApprovalItem,
  ApprovalOutcome,
  ApprovalRules,
  ApprovalRulesUpdate,
  Retirement,
  Candidate,
  CandidateConfirmation,
  Component,
  ComponentCreate,
  ComponentLocation,
  ComponentLocationCreate,
  ComponentMemory,
  Change,
  DecisionProposal,
  DriftReport,
  Gate,
  PilotMetrics,
  RepositorySnapshot,
  LineageRecord,
  Review,
  Significance,
  Why,
  Constraint,
  Decision,
  DecisionCreate,
  DecisionEvent,
  DecisionImport,
  DecisionImportResult,
  DecisionLineage,
  DecisionUpdate,
  ErrorBody,
  ErrorCode,
  JobStatus,
  MarkdownBundle,
  MarkdownFile,
  OnboardingRun,
  OperationRef,
} from "@edm/contracts";

export class ApiRequestError extends Error {
  readonly code: ErrorCode | "http_error" | "network_error";
  readonly requestId: string | undefined;
  readonly details: unknown;
  constructor(
    readonly status: number,
    body: Partial<ErrorBody>,
  ) {
    super(body.message ?? `HTTP ${status}`);
    this.name = "ApiRequestError";
    this.code = (body.code as ErrorCode) ?? "http_error";
    this.requestId = body.requestId;
    this.details = body.details;
  }
}

export function isApiError(err: unknown, code?: ErrorCode | "http_error" | "network_error"): err is ApiRequestError {
  return err instanceof ApiRequestError && (code === undefined || err.code === code);
}

export interface Me {
  actor: {
    kind: "user" | "api_key";
    id: string;
    email?: string;
    name?: string;
    username?: string | null;
    image?: string | null;
    /** Interface preferences stored with the account; null until the person picks one. */
    locale?: string | null;
    appearance?: string | null;
  };
  workspaces: WorkspaceSummary[];
}
export interface WorkspaceSummary {
  id: string;
  slug: string;
  name: string;
  /** Avatar URL under /api/user-content; null when the workspace has none. */
  logo?: string | null;
  createdAt?: string;
  /** Absent once the workspace is set up (D-0022); otherwise why its pages wait. */
  setup?: "pending" | "skipped" | "running";
}
/** One read during setup or from a connector's page: uploaded files, or an exploration of connectors (D-0022, D-0023). */
export interface SourceSync {
  id: string;
  /** `upload`, or `explore` with the connectors in `sources`. */
  source: string;
  sources: string[];
  status: "queued" | "reading" | "processing" | "succeeded" | "failed";
  requestedAt: string;
  finishedAt: string | null;
  documents: number | null;
  /** Pages left out, by reason. */
  skipped: Partial<Record<"restricted" | "overLimit" | "unreadable", number>>;
  /** Per connector, what an exploration saw listed and what it opened. */
  coverage: Record<string, { listed: number; read: number }>;
  /** How many decisions an exploration suggested. */
  candidates: number | null;
  /** A short code the page knows (no_pages, unauthorized, rate_limited, timed_out) or the source's own message. */
  error: string | null;
}
export interface WorkspaceSetup {
  state: "pending" | "skipped" | "running" | "ready";
  syncs: SourceSync[];
}
export type WorkspaceUpdate = Partial<{ name: string; slug: string; logo: string | null }>;
export interface WorkspaceDetail extends WorkspaceSummary {
  members: { id: string; userId: string; role: string; email: string; name: string }[];
  pendingInvitations: { id: string; email: string; role: string; expiresAt: string }[];
}
export interface ApiKeySummary {
  id: string;
  name: string | null;
  start: string | null;
  enabled: boolean;
  expiresAt: string | null;
  createdAt: string;
  permissions: Record<string, string[]> | null;
}
/** What an installation covers on the provider (repositories…). `all` has no list by design: it includes whatever comes later. */
export interface ConnectorResources {
  kind: string;
  selection: "all" | "selected";
  /** Capped by the server; `total` may be larger. */
  items: string[];
  total: number;
  /** `total` is a floor: the server stopped counting there. */
  truncated?: boolean;
}
export interface ConnectorInstallation {
  externalTeamId: string;
  externalTeamName: string;
  installedBy: string;
  installedAt: string;
  /** Paused on the provider: still installed, nothing arrives until it is resumed there. */
  suspended?: boolean;
  /** The provider's own settings page for this installation. */
  manageUrl?: string;
  resources?: ConnectorResources;
}
/** A platform the agent works in (D-0018, D-0019). `configured` false: this deployment cannot install it at all. */
export interface ConnectorSummary {
  provider: string;
  name: string;
  /** From the connector's own manifest, in every language it was written in; `detail` is Markdown. */
  logoUrl?: string;
  description?: Record<string, string>;
  detail?: Record<string, string>;
  configured: boolean;
  /** People can prove an account on this provider is theirs from the connectors page. */
  canIdentify?: boolean;
  /** Someone asked an owner on the provider's side to install; nothing is connected yet. */
  pending?: boolean;
  installation: ConnectorInstallation | null;
  links: ConnectorLink[];
  /** Its pages can be read into memory (D-0022). */
  readsPages?: boolean;
  /** The latest read of its pages, if there was one. */
  lastSync?: SourceSync | null;
}
export interface ConnectorLink {
  externalUserId: string;
  externalName: string;
  userId: string;
  method: "email" | "manual" | "account" | "oauth";
  linkedAt: string;
  /** Null when the person has left the workspace: the link grants nothing. */
  member: { name: string; email: string; image: string | null; role: string } | null;
}
export interface ConnectorLinkRequest {
  provider: string;
  providerName: string;
  externalName: string;
  externalTeamName: string;
  workspace: { slug: string; name: string };
}
export type PageOf<T> = { items: T[]; nextCursor: string | null };
/** A coding agent a person connected to a workspace over MCP (D-0021). Only ever the caller's own. */
export interface AgentConnection {
  id: string;
  clientId: string;
  name: string;
  icon: string | null;
  uri: string | null;
  connectedAt: string;
}
/** What an OAuth client says about itself (Better Auth's public client fields). */
export interface OAuthClientInfo {
  client_id: string;
  client_name?: string;
  client_uri?: string;
  logo_uri?: string;
}
export type DecisionPage = PageOf<Decision>;
export type TransitionTarget = "proposed" | "accepted" | "rejected" | "deprecated";

export type Fetch = typeof fetch;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export function createApi(doFetch: Fetch = (...args) => fetch(...args)) {
  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json" };
    if (opts.body !== undefined) headers["content-type"] = "application/json";
    if (opts.idempotencyKey) headers["idempotency-key"] = opts.idempotencyKey;
    let res: Response;
    try {
      res = await doFetch(`/api${path}`, {
        method: opts.method ?? "GET",
        credentials: "same-origin",
        headers,
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
        ...(opts.signal ? { signal: opts.signal } : {}),
      });
    } catch (err) {
      throw new ApiRequestError(0, { code: "network_error" as ErrorCode, message: err instanceof Error ? err.message : "network error" });
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    if (!res.ok) throw new ApiRequestError(res.status, (body as Partial<ErrorBody> | null) ?? { message: res.statusText });
    return body as T;
  }

  const w = (workspaceId: string) => `/w/${encodeURIComponent(workspaceId)}`;

  return {
    me: () => request<Me>("/me"),
    listWorkspaces: () => request<PageOf<WorkspaceSummary>>("/workspaces"),
    /** Uploads a cropped picture (data URL) to user content, or clears it; returns the stored URL. */
    updateAvatar: (image: string | null) => request<{ image: string | null }>("/me/avatar", { method: "PUT", body: { image } }),
    // Linking a chat-platform account by hand: the token comes from the agent's message.
    inspectConnectorLink: (token: string) => request<ConnectorLinkRequest>(`/connectors/link?token=${encodeURIComponent(token)}`),
    completeConnectorLink: (token: string) => request<{ workspace: { slug: string; name: string } }>("/connectors/link", { method: "POST", body: { token } }),
    createWorkspace: (input: { name: string; slug: string }) => request<WorkspaceSummary>("/workspaces", { method: "POST", body: input }),
    // Connecting a coding agent (D-0021): the consent page reads who is asking, then answers for one workspace.
    oauthClient: (clientId: string) => request<OAuthClientInfo>(`/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`),
    consentToAgent: (input: { accept: boolean; workspaceId?: string; oauthQuery: string }) => request<{ redirectTo: string }>("/oauth/consent", { method: "POST", body: input }),
    workspace: (workspaceId: string) => ({
      get: () => request<WorkspaceDetail>(w(workspaceId)),
      update: (input: WorkspaceUpdate) => request<WorkspaceSummary>(w(workspaceId), { method: "PATCH", body: input }),
      listDecisions: (cursor?: string, filter: { status?: string; limit?: number } = {}) => {
        const q = new URLSearchParams();
        if (cursor) q.set("cursor", cursor);
        if (filter.status) q.set("status", filter.status);
        if (filter.limit) q.set("limit", String(filter.limit));
        const qs = q.toString();
        return request<DecisionPage>(`${w(workspaceId)}/decisions${qs ? `?${qs}` : ""}`);
      },
      getDecision: (id: string) => request<Decision>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}`),
      createDecision: (input: DecisionCreate) => request<Decision>(`${w(workspaceId)}/decisions`, { method: "POST", body: input, idempotencyKey: crypto.randomUUID() }),
      updateDecision: (id: string, input: DecisionUpdate) =>
        request<Decision>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}`, { method: "PATCH", body: input, idempotencyKey: crypto.randomUUID() }),
      transitionDecision: (id: string, status: TransitionTarget, reason?: string) =>
        request<Decision>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/transition/${status}`, { method: "POST", body: reason ? { reason } : {}, idempotencyKey: crypto.randomUUID() }),
      importDecisions: (input: DecisionImport) =>
        request<DecisionImportResult>(`${w(workspaceId)}/decisions/import`, { method: "POST", body: input, idempotencyKey: crypto.randomUUID() }),
      exportDecisions: () => request<MarkdownBundle>(`${w(workspaceId)}/decisions/export`),
      decisionMarkdown: (id: string) => request<MarkdownFile>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/markdown`),
      listComponents: () => request<PageOf<Component>>(`${w(workspaceId)}/components?limit=200`),
      createComponent: (input: ComponentCreate) => request<Component>(`${w(workspaceId)}/components`, { method: "POST", body: input, idempotencyKey: crypto.randomUUID() }),
      componentMemory: (id: string) => request<ComponentMemory>(`${w(workspaceId)}/components/${encodeURIComponent(id)}/memory`),
      addComponentLocation: (id: string, input: ComponentLocationCreate) =>
        request<ComponentLocation>(`${w(workspaceId)}/components/${encodeURIComponent(id)}/locations`, { method: "POST", body: input, idempotencyKey: crypto.randomUUID() }),
      resolveLocation: (locationId: string, confirm: boolean) =>
        request<ComponentLocation>(`${w(workspaceId)}/component-locations/${encodeURIComponent(locationId)}/${confirm ? "confirm" : "reject"}`, { method: "POST", idempotencyKey: crypto.randomUUID() }),
      pilotMetrics: () => request<PilotMetrics>(`${w(workspaceId)}/metrics/pilot`),
      driftFindings: () => request<DriftReport>(`${w(workspaceId)}/drift`),
      detectDrift: () => request<DriftReport>(`${w(workspaceId)}/drift`, { method: "POST", idempotencyKey: crypto.randomUUID() }),
      resolveDrift: (id: string, outcome: "confirm" | "dismiss", reason?: string) =>
        request<DriftReport["findings"][number]>(`${w(workspaceId)}/drift/${encodeURIComponent(id)}/resolve`, { method: "POST", body: { outcome, ...(reason ? { reason } : {}) }, idempotencyKey: crypto.randomUUID() }),
      changeGate: (id: string) => request<Gate>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}/gate`),
      // The page is /lineage, but the contract route stays /why (contracts/http/v1/why.schema.json,
      // the agent operation catalog and core-engine all name it): renaming it is a breaking change.
      why: (repository: string, path: string) => {
        const q = new URLSearchParams({ path, ...(repository ? { repository } : {}) });
        return request<Why>(`${w(workspaceId)}/why?${q}`);
      },
      changeSignificance: (id: string) => request<Significance>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}/significance`),
      proposeDecision: (id: string, force = false) =>
        request<DecisionProposal>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}/proposal`, { method: "POST", body: force ? { force } : {}, idempotencyKey: crypto.randomUUID() }),
      changeLineage: (id: string) => request<LineageRecord>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}/lineage`),
      listChanges: () => request<PageOf<Change>>(`${w(workspaceId)}/changes?limit=100`),
      getChange: (id: string) => request<Change>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}`),
      changeReview: (id: string) => request<Review>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}/review`),
      runReview: (id: string) => request<Review>(`${w(workspaceId)}/changes/${encodeURIComponent(id)}/review`, { method: "POST", idempotencyKey: crypto.randomUUID() }),
      resolveFinding: (id: string, outcome: "confirm" | "dismiss" | "accept_exception", reason?: string) =>
        request<Review["findings"][number]>(`${w(workspaceId)}/review-findings/${encodeURIComponent(id)}/resolve`, { method: "POST", body: { outcome, ...(reason ? { reason } : {}) }, idempotencyKey: crypto.randomUUID() }),
      onboardingRuns: (repository?: string) => request<PageOf<OnboardingRun>>(`${w(workspaceId)}/ingestion/runs${repository ? `?repository=${encodeURIComponent(repository)}` : ""}`),
      candidates: (filter: { kind?: "decision_candidate" | "constraint_candidate" | "component_candidate"; status?: string; runId?: string } = {}) => {
        const q = new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => Boolean(e[1])));
        return request<PageOf<Candidate>>(`${w(workspaceId)}/candidates${q.size ? `?${q}` : ""}`);
      },
      confirmCandidate: (id: string, activate = false) =>
        request<CandidateConfirmation>(`${w(workspaceId)}/candidates/${encodeURIComponent(id)}/confirm`, { method: "POST", body: activate ? { activate } : {}, idempotencyKey: crypto.randomUUID() }),
      rejectCandidate: (id: string, reason?: string) =>
        request<Candidate>(`${w(workspaceId)}/candidates/${encodeURIComponent(id)}/reject`, { method: "POST", body: reason ? { reason } : {}, idempotencyKey: crypto.randomUUID() }),
      decisionHistory: (id: string) => request<PageOf<DecisionEvent>>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/history`),
      decisionLineage: (id: string) => request<DecisionLineage>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/lineage`),
      listAmendments: (id: string) => request<PageOf<Amendment>>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/amendments`),
      proposeAmendment: (id: string, input: AmendmentCreate) =>
        request<Amendment>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/amendments`, { method: "POST", body: input, idempotencyKey: crypto.randomUUID() }),
      decideAmendment: (id: string, number: number, accept: boolean, reason?: string) =>
        request<Amendment>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/amendments/${number}/${accept ? "accept" : "reject"}`, {
          method: "POST",
          body: reason ? { reason } : {},
          idempotencyKey: crypto.randomUUID(),
        }),
      // Approval rules, the queue of write requests, votes and retirement requests (D-0016).
      // A vote takes effect only once the workspace's rules are met; the outcome says whether it did.
      approvalRules: () => request<ApprovalRules>(`${w(workspaceId)}/approval-rules`),
      setApprovalRules: (input: ApprovalRulesUpdate) =>
        request<ApprovalRules>(`${w(workspaceId)}/approval-rules`, { method: "PUT", body: input, idempotencyKey: crypto.randomUUID() }),
      approvalQueue: () => request<PageOf<ApprovalItem>>(`${w(workspaceId)}/approval-queue`),
      approveDecision: (id: string) =>
        request<ApprovalOutcome>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/approvals`, { method: "POST", body: {}, idempotencyKey: crypto.randomUUID() }),
      approveAmendment: (id: string, number: number) =>
        request<ApprovalOutcome>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/amendments/${number}/approvals`, { method: "POST", body: {}, idempotencyKey: crypto.randomUUID() }),
      // Whoever put a proposal or an amendment up takes it back (D-0016.A1); a proposal returns to draft.
      withdrawDecision: (id: string, reason?: string) =>
        request<Decision>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/withdraw`, { method: "POST", body: reason ? { reason } : {}, idempotencyKey: crypto.randomUUID() }),
      withdrawAmendment: (id: string, number: number, reason?: string) =>
        request<Amendment>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/amendments/${number}/withdraw`, {
          method: "POST",
          body: reason ? { reason } : {},
          idempotencyKey: crypto.randomUUID(),
        }),
      listRetirements: (id: string) => request<PageOf<Retirement>>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/retirements`),
      proposeRetirement: (id: string, reason: string) =>
        request<Retirement>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/retirements`, { method: "POST", body: { reason }, idempotencyKey: crypto.randomUUID() }),
      approveRetirement: (id: string, number: number) =>
        request<ApprovalOutcome>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/retirements/${number}/approvals`, { method: "POST", body: {}, idempotencyKey: crypto.randomUUID() }),
      closeRetirement: (id: string, number: number, how: "decline" | "withdraw", reason?: string) =>
        request<Retirement>(`${w(workspaceId)}/decisions/${encodeURIComponent(id)}/retirements/${number}/${how}`, {
          method: "POST",
          body: reason ? { reason } : {},
          idempotencyKey: crypto.randomUUID(),
        }),
      listConstraints: (filter: { introducedBy?: string; status?: string } = {}) => {
        const q = new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => Boolean(e[1])));
        return request<PageOf<Constraint>>(`${w(workspaceId)}/constraints${q.size ? `?${q}` : ""}`);
      },
      getOperation: (id: string, signal?: AbortSignal) => request<JobStatus>(`${w(workspaceId)}/operations/${encodeURIComponent(id)}`, signal ? { signal } : {}),
      runSmoke: (payload: Record<string, unknown> = {}) => request<OperationRef>(`${w(workspaceId)}/operations/smoke`, { method: "POST", body: payload, idempotencyKey: crypto.randomUUID() }),
      listApiKeys: () => request<PageOf<ApiKeySummary>>(`${w(workspaceId)}/api-keys`),
      createApiKey: (input: { name: string; permissions?: Record<string, string[]> }) => request<{ id: string; name: string; key: string }>(`${w(workspaceId)}/api-keys`, { method: "POST", body: input }),
      deleteApiKey: (keyId: string) => request<void>(`${w(workspaceId)}/api-keys/${encodeURIComponent(keyId)}`, { method: "DELETE" }),
      listAgentConnections: () => request<PageOf<AgentConnection> & { mcpUrl: string }>(`${w(workspaceId)}/agent-connections`),
      disconnectAgent: (id: string) => request<void>(`${w(workspaceId)}/agent-connections/${encodeURIComponent(id)}`, { method: "DELETE" }),
      listConnectors: () => request<PageOf<ConnectorSummary>>(`${w(workspaceId)}/connectors`),
      /** Answers the provider's install URL; the browser goes there and comes back to `returnTo`. */
      installConnector: (provider: string, returnTo: string) => request<{ url: string }>(`${w(workspaceId)}/connectors/${encodeURIComponent(provider)}/install`, { method: "POST", body: { returnTo } }),
      identifyConnectorAccount: (provider: string, returnTo: string) => request<{ url: string }>(`${w(workspaceId)}/connectors/${encodeURIComponent(provider)}/identify`, { method: "POST", body: { returnTo } }),
      removeConnector: (provider: string) => request<void>(`${w(workspaceId)}/connectors/${encodeURIComponent(provider)}`, { method: "DELETE" }),
      syncConnector: (provider: string) => request<SourceSync>(`${w(workspaceId)}/connectors/${encodeURIComponent(provider)}/sync`, { method: "POST", body: {} }),
      // Setting the workspace up (D-0022): read its sources once, or say there are none.
      setup: () => request<WorkspaceSetup>(`${w(workspaceId)}/setup`),
      startSetup: (input: { sources: string[]; upload?: RepositorySnapshot }) => request<WorkspaceSetup>(`${w(workspaceId)}/setup/start`, { method: "POST", body: input }),
      skipSetup: () => request<WorkspaceSetup>(`${w(workspaceId)}/setup/skip`, { method: "POST", body: {} }),
      delegateSetup: () => request<WorkspaceSetup>(`${w(workspaceId)}/setup/agent`, { method: "POST", body: {} }),
      unlinkConnectorUser: (provider: string, externalUserId: string) =>
        request<void>(`${w(workspaceId)}/connectors/${encodeURIComponent(provider)}/links/${encodeURIComponent(externalUserId)}`, { method: "DELETE" }),
    }),
  };
}

export type Api = ReturnType<typeof createApi>;
export type WorkspaceApi = ReturnType<Api["workspace"]>;
export const api: Api = createApi();
